/**
 * Provider-neutral Devices inventory API client (`/api/devices*`).
 * Route identity is always `(sourceType, sourceId, deviceId)`.
 */
import HttpClient from "./httpClient";

export type DeviceSourceType = "platform" | "application";

export interface DeviceCapabilities {
  live_status?: boolean;
  utilization_history?: boolean;
  theo_alert_history?: boolean;
  software?: boolean;
  operations?: boolean;
  patching?: boolean;
  remote_session?: boolean;
  last_check_in?: boolean;
  compliance?: boolean;
  encryption?: boolean;
  ownership?: boolean;
  management?: boolean;
  hardware?: boolean;
  storage?: boolean;
  primary_user?: boolean;
  device_category?: boolean;
  applications?: boolean;
  device_chat?: boolean;
  [key: string]: boolean | undefined;
}

export interface DeviceOngoingIssueCount {
  issue_type: string;
  issue_type_label?: string;
  ticket_count: number;
  instance_count: number;
  start_ms: number;
  last_seen_ms: number;
}

/** One fleet row from GET /api/devices — always keyed by device_key, never external_device_id alone. */
export interface DeviceRow {
  device_key: string;
  external_device_id: string;
  host_name: string;
  provider: string;
  source_type: DeviceSourceType | string;
  source_id: number;
  customer_id: number | null;
  customer_name: string;
  user?: string | null;
  os_family?: string | null;
  operating_system?: string | null;
  online: boolean | null;
  last_seen_ms: number;
  /** Intune compliance_state when online is null; unused for RMM rows. */
  compliance_state?: string | null;
  capabilities?: DeviceCapabilities | null;
  ongoing_issue_count: number;
  ongoing_issue_counts?: DeviceOngoingIssueCount[];
}

export interface DeviceSourceStatus {
  source_type: DeviceSourceType | string;
  source_id: number;
  provider: string;
  customer_id: number | null;
  customer_name?: string | null;
  generated_at_ms: number;
  device_count: number;
  status: string;
  /** Sanitized refresh failure `{ message, status?, code? }`. */
  error?: { message?: string; status?: number | null; code?: string | null } | string | null;
  last_refresh_failed_at_ms?: number | null;
}

export interface DeviceListQuery {
  page?: number;
  page_size?: number;
  hostname?: string;
  user?: string;
  os?: string;
  status?: string;
  customer?: string;
  provider?: string;
  sort?: "host_name" | "os_family" | "last_seen_ms" | "online" | "customer" | "user" | "provider";
  order?: "asc" | "desc";
  prioritize_issues?: boolean;
}

export interface DeviceListFacets {
  customers: Array<{ value: string; label: string }>;
  providers: string[];
  os_families: string[];
  statuses: string[];
}

export interface DevicesListResponse {
  page: number;
  page_size: number;
  filtered_count: number;
  facets: DeviceListFacets;
  provider_counts: Record<string, number>;
  generated_at_ms: number;
  device_count: number;
  devices: DeviceRow[];
  sources?: DeviceSourceStatus[];
  rmm_alerts_enabled?: boolean;
  error?: string | null;
}

export interface DevicesRefreshResponse extends DevicesListResponse {
  success?: boolean;
  partial_failure?: boolean;
}

export interface DeviceSnapshotDiskMetric {
  disk_identifier?: string;
  description?: string;
  size_bytes?: number | null;
  free_bytes?: number | null;
  used_bytes?: number | null;
  used_percent?: number | null;
}

export interface DeviceSnapshotMetrics {
  online?: boolean | null;
  last_seen_ms?: number | null;
  last_reboot_ms?: number | null;
  last_audit_ms?: number | null;
  reboot_required?: boolean | null;
  suspended?: boolean | null;
  deleted?: boolean | null;
  patch_management?: Record<string, unknown> | null;
  antivirus?: Record<string, unknown> | null;
  software_status?: string | null;
  software_count?: number | null;
  cpu?: {
    total_cores?: number | null;
    processor_count?: number | null;
    processors?: string[];
    used_percent?: number | null;
    usage_source?: string | null;
  } | null;
  memory?: {
    total_bytes?: number | null;
    free_bytes?: number | null;
    used_bytes?: number | null;
    used_percent?: number | null;
    free_percent?: number | null;
    usage_source?: string | null;
    module_count?: number | null;
    modules?: Array<Record<string, unknown>>;
  } | null;
  disks?: DeviceSnapshotDiskMetric[] | null;
  open_alerts?: {
    count?: number | null;
    by_priority?: Record<string, number>;
    by_context_type?: Record<string, number>;
    latest_alert_ms?: number | null;
  } | null;
  network?: Record<string, unknown>;
  [key: string]: unknown;
}

export type DeviceDetailSectionName = "device" | "audit" | "software" | "alerts";
export interface DeviceDetailSection {
  state: "ready" | "stale" | "unavailable" | "unsupported";
  sampled_at_ms: number | null;
  checked_at_ms: number;
  error?: { message?: string; status?: number | null } | null;
}

export interface DeviceSnapshotSample {
  provider?: string;
  msp_id?: number;
  asset_id?: string;
  host_name?: string | null;
  sampled_at_ms: number;
  audit_kind?: string;
  sections?: Partial<Record<DeviceDetailSectionName, DeviceDetailSection>>;
  metrics?: DeviceSnapshotMetrics;
  raw?: Record<string, unknown> | null;
  [key: string]: unknown;
}

export interface DeviceDetailRefreshStatus {
  state: "queued" | "running" | "retrying" | "succeeded" | "failed";
  requested_at_ms: number;
  updated_at_ms: number;
  next_attempt_at_ms?: number | null;
  completed_at_ms?: number | null;
  attempts: number;
  error?: { message?: string; status?: number | null; retryAfterMs?: number | null } | null;
}

export interface DeviceSnapshotDetailsResponse {
  msp_id: number;
  source?: {
    source_type?: string;
    source_id?: number;
    provider?: string;
    customer_id?: number | null;
    customer_name?: string | null;
    [key: string]: unknown;
  };
  device_key?: string;
  device_id: string;
  provider?: string;
  generated_at_ms: number;
  metadata: Record<string, unknown> | null;
  status: Record<string, unknown> | null;
  latest_sample: DeviceSnapshotSample | null;
  samples: DeviceSnapshotSample[];
  sample_count?: number;
  history_limit?: number;
  capabilities?: DeviceCapabilities | null;
  refresh_status?: DeviceDetailRefreshStatus | null;
  stale?: boolean;
  refreshing?: boolean;
  refreshed?: boolean;
  refresh_error?: { message?: string; status?: number | null; code?: string | null } | null;
  error?: string | null;
}

export interface DeviceAlertInstance {
  alert_uid?: string;
  issue_type?: string;
  issue_type_label?: string;
  priority?: string;
  start_ms?: number;
  last_seen_ms?: number;
  end_ms?: number | null;
  tickets?: Array<{
    ticket_id: string;
    summary?: string;
    created_ms?: number;
    metric?: { value?: number | null; unit?: string | null } | null;
  }>;
}

export interface DeviceAlertsResponse {
  source_type?: string;
  source_id?: number;
  provider: string;
  msp_id: number;
  device_id: string;
  lookback_days?: number;
  instances: DeviceAlertInstance[];
  code?: string;
  error?: string;
}

export interface DeviceThreadSummary {
  ai_thread_id: number;
  user_id?: string | null;
  thread_name?: string | null;
  created_at_ms?: number;
  updated_at_ms?: number;
  latest_message_at_ms?: number;
  message_count?: number;
}

export interface DeviceThreadResponse {
  ai_thread?: {
    ai_thread_id: string | number;
    user_id?: { value?: string } | string | null;
    ticket_id?: { value?: string } | string | null;
    device_source_type?: { value?: string } | string | null;
    device_source_id?: { value?: number } | number | null;
    device_provider?: { value?: string } | string | null;
    device_external_id?: { value?: string } | string | null;
    customer_id?: { value?: number } | number | null;
    thread_name?: { value?: string } | string | null;
    messages?: any[];
    [key: string]: unknown;
  };
  source_type?: string;
  source_id?: number;
  provider?: string;
  created?: boolean;
  threads?: DeviceThreadSummary[];
  [key: string]: unknown;
}

export interface DeviceThreadsResponse {
  source_type?: string;
  source_id?: number;
  provider: string;
  msp_id: number;
  device_id: string;
  threads: DeviceThreadSummary[];
}

export interface DeviceDeleteThreadResponse {
  success?: boolean;
  error_message?: string;
  [key: string]: unknown;
}

export interface DeviceIdentityParams {
  sourceType: string;
  sourceId: number | string;
  deviceId: string;
}

export interface DeviceAlertSummary {
  generated_at_ms: number;
  history: Array<{ days: number; count: number }>;
  customer_id: number | null;
  customer_issues: Array<{ issue_type: string; count: number }>;
}

export interface AlertTicketQuery {
  scope: "device" | "customer";
  days: number;
  issue_type?: string;
  offset?: number;
}

export interface AlertTicketPage {
  total: number;
  offset: number;
  tickets: Array<{
    platform_id: number; ticket_id: string; display_id: string; title: string;
    host_name: string; issue_type: string; created_ms: number;
  }>;
}

function devicePath({ sourceType, sourceId, deviceId }: DeviceIdentityParams, suffix = ""): string {
  return `/api/devices/${encodeURIComponent(String(sourceType))}/${encodeURIComponent(String(sourceId))}/${encodeURIComponent(deviceId)}${suffix}`;
}

class DevicesApi extends HttpClient {
  async getAlertSummary(identity: DeviceIdentityParams): Promise<DeviceAlertSummary> {
    const response = await this.instance.get(devicePath(identity, "/alert-summary"));
    if (response.status >= 400) throw Object.assign(new Error(response.data?.error || "Unable to load alert history"), { status: response.status });
    return response.data.data;
  }

  async getAlertTickets(identity: DeviceIdentityParams, query: AlertTicketQuery): Promise<AlertTicketPage> {
    const response = await this.instance.get(devicePath(identity, "/alert-tickets"), { params: query });
    if (response.status >= 400) throw new Error(response.data?.error || "Unable to load alert tickets");
    return response.data.data;
  }

  async listDevices(query: DeviceListQuery = {}): Promise<DevicesListResponse> {
    const response = await this.instance.get("/api/devices", { params: query });
    if (response.status >= 400) {
      throw new Error(response.data?.error || "Failed to load devices");
    }
    return response.data?.data as DevicesListResponse;
  }

  async refreshDevices(query: DeviceListQuery = {}): Promise<DevicesRefreshResponse> {
    const response = await this.instance.post("/api/devices/refresh", {}, { params: query });
    if (response.status >= 400) {
      throw new Error(response.data?.error || "Unable to refresh devices. Try Refresh again in a moment.");
    }
    return response.data?.data as DevicesRefreshResponse;
  }

  async getDeviceDetails(identity: DeviceIdentityParams, limit = 288): Promise<DeviceSnapshotDetailsResponse> {
    const response = await this.instance.get(devicePath(identity, "/details"), { params: { limit } });
    if (response.status >= 400) {
      throw Object.assign(new Error(response.data?.error || "Failed to load device details"), { response });
    }
    return response.data?.data as DeviceSnapshotDetailsResponse;
  }

  async getDeviceRefreshStatus(identity: DeviceIdentityParams): Promise<DeviceDetailRefreshStatus | null> {
    const response = await this.instance.get(devicePath(identity, "/refresh-status"));
    if (response.status >= 400) throw Object.assign(new Error(response.data?.error || "Unable to check device refresh status"), { response });
    return response.data.data;
  }

  /**
   * Refresh the selected device through its owning integration and return updated cached details.
   */
  async refreshDeviceDetails(identity: DeviceIdentityParams, limit = 288): Promise<DeviceSnapshotDetailsResponse> {
    const response = await this.instance.post(devicePath(identity, "/refresh"), { limit });
    if (response.status >= 400) {
      throw new Error(response.data?.error || "Failed to refresh device details");
    }
    return response.data?.data as DeviceSnapshotDetailsResponse;
  }

  async listDeviceAlerts(identity: DeviceIdentityParams): Promise<DeviceAlertsResponse> {
    const response = await this.instance.get(devicePath(identity, "/alerts"));
    if (response.status >= 400) {
      throw new Error(response.data?.error || "Failed to load device alerts");
    }
    return response.data?.data as DeviceAlertsResponse;
  }

  async getOrCreateDeviceThread(identity: DeviceIdentityParams): Promise<DeviceThreadResponse> {
    const response = await this.instance.get(devicePath(identity, "/thread"));
    if (response.status >= 400) {
      throw new Error(response.data?.error || "Failed to load device thread");
    }
    return response.data?.data as DeviceThreadResponse;
  }

  async getDeviceThread(identity: DeviceIdentityParams, aiThreadId: string): Promise<DeviceThreadResponse> {
    const response = await this.instance.get(
      devicePath(identity, `/thread/${encodeURIComponent(aiThreadId)}`)
    );
    if (response.status >= 400) {
      throw new Error(response.data?.error || "Failed to load device thread");
    }
    return response.data?.data as DeviceThreadResponse;
  }

  async listDeviceThreads(identity: DeviceIdentityParams): Promise<DeviceThreadsResponse> {
    const response = await this.instance.get(devicePath(identity, "/threads"));
    if (response.status >= 400) {
      throw new Error(response.data?.error || "Failed to list device threads");
    }
    return response.data?.data as DeviceThreadsResponse;
  }

  async createDeviceThread(identity: DeviceIdentityParams): Promise<DeviceThreadResponse> {
    const response = await this.instance.post(devicePath(identity, "/thread"), {});
    if (response.status >= 400) {
      throw new Error(response.data?.error || "Failed to create device thread");
    }
    return response.data?.data as DeviceThreadResponse;
  }

  async deleteDeviceThread(
    identity: DeviceIdentityParams,
    aiThreadId: string
  ): Promise<DeviceDeleteThreadResponse> {
    const response = await this.instance.delete(
      devicePath(identity, `/thread/${encodeURIComponent(aiThreadId)}`)
    );
    if (response.status >= 400) {
      throw new Error(response.data?.error || "Failed to delete device thread");
    }
    return response.data?.data as DeviceDeleteThreadResponse;
  }
}

export default new DevicesApi();
